export const formTemplateGenericSort = (
  a: { value: { sort: number } },
  b: { value: { sort: number } }
) => {
  return a.value.sort - b.value.sort;
};

export enum Compare {
  equals = 'equals',
  lessThan = '<',
  lessThanOrEqual = '<=',
  greaterThan = '>',
  greaterThanOrEqual = '>='
}

export enum TriggerGroupType {
  and = 'and',
  or = 'or'
}

export interface Trigger {
  key: string;
  compareValue: any;
  compare: Compare;
}

export interface TriggerGroup {
  type: TriggerGroupType;
  triggers: Trigger[];
}

export interface FormTemplateGroup {
  title: string;
  nextLabel: string;
  formFields: { [key: string]: FormFieldConfig };
  customComponent?: 'PatientCard';
  sort?: number;
}

export interface FormFieldConfig {
  label: string;
  typeConfig: FormFieldTypeConfig;
  required?: boolean;
  triggerGroups?: TriggerGroup[];
  sort?: number;
  /** When true, value is collected by clinic staff at send time and locked for the patient. */
  clinicianOnly?: boolean;
}

export enum TextInputType {
  text = 'text',
  number = 'number',
  email = 'email',
  tel = 'tel'
}

export interface ValueConfig {
  label: string;
  showSubField?: boolean;
  subFieldIdList?: { id: string; required: boolean }[];
  subFieldTitle?: string;
  sort?: number;
}

export type SpecialOptionValueConfig = ValueConfig & {
  isSpecialOption?: boolean;
};

type EnforceUnionExtends<TUnion, TInterface> = TUnion extends TInterface ? TUnion : never;

export type FormFieldTypeConfig = EnforceUnionExtends<
  | TextConfig
  | DropDownConfig
  | RadioConfig
  | SliderConfig
  | CheckboxConfig
  | QuestionGroupConfig
  | DateConfig
  | ListConfig
  | PhoneConfig
  | EmailConfig
  | HealthCardConfig
  | AttachmentsConfig
  | PhoneAndCountryConfig
  | MedicationLookupConfig
  | LegalTextConfig
  | InitialsConfig
  | SignatureConfig
  | AcknowledgementConfig,
  { type: ConfigType }
>;

export enum ConfigType {
  input = 'input',
  dropdown = 'dropdown',
  phone = 'phone',
  email = 'email',
  radio = 'radio',
  questionGroup = 'questionGroup',
  slider = 'slider',
  checkbox = 'checkbox',
  date = 'date',
  list = 'list',
  healthcard = 'healthcard',
  attachments = 'attachments',
  medications = 'medications',
  phoneAndCountryCode = 'phoneAndCountryCode',
  legalText = 'legalText',
  initials = 'initials',
  signature = 'signature',
  acknowledgement = 'acknowledgement'
}

export interface AttachmentsConfig {
  type: ConfigType.attachments;
  maxAttachments: number;
}

export interface HealthCardConfig {
  type: ConfigType.healthcard;
}

export interface MedicationLookupConfig {
  type: ConfigType.medications;
}

export interface TextConfig {
  type: ConfigType.input;
  multiLine: boolean;
  title?: string;
  specialOptions?: { [key in SpecialValueKey]: SpecialOptionValueConfig };
}

export interface ListConfig {
  type: ConfigType.list;
  itemName: string;
}

export interface DropDownConfig {
  type: ConfigType.dropdown;
  valuesConfig: { [key: string]: ValueConfig };
}

export interface PhoneConfig {
  type: ConfigType.phone;
}

export interface PhoneAndCountryConfig {
  type: ConfigType.phoneAndCountryCode;
}

export interface EmailConfig {
  type: ConfigType.email;
  inputType: 'email';
}

export interface RadioConfig {
  type: ConfigType.radio;
  valuesConfig: { [key: string]: ValueConfig };
}

export interface SliderConfig {
  type: ConfigType.slider;
  min: number;
  max: number;
  step: number;
  subLabel?: string;
  minLabel?: string;
  maxLabel?: string;
  showPlusSign?: boolean;
  firstLabelIndex?: number;
  alwaysDisplayBottomLabelEndpoints?: boolean;
  bottomLabels?: string[];
}

export enum SpecialValueKey {
  NONE = 'NONE'
}

export interface CheckboxConfig {
  type: ConfigType.checkbox;
  valuesConfig:
    | { [key: string]: ValueConfig }
    | { [key in SpecialValueKey]: SpecialOptionValueConfig };
  showNoneOptionConfig?: {
    label: string;
  };
  columnType?: 'single' | 'double';
}

export interface QuestionGroupConfig {
  type: ConfigType.questionGroup;
  formFields: { [key: string]: FormFieldConfig };
}

export interface DateConfig {
  type: ConfigType.date;
  maxDateToday?: boolean;
  showNotApplicableOption: boolean;
  /** When true, value is set to today on load and the control is read-only for the patient. */
  lockedToToday?: boolean;
}

export interface CustomComponentGroup {
  label: string;
  componentName: 'PatientCard';
  controlKeys: string[];
}

export interface LegalTextConfig {
  type: ConfigType.legalText;
  /** Plain text or simple HTML paragraphs shown read-only */
  content: string;
}

export interface InitialsConfig {
  type: ConfigType.initials;
  maxLength?: number;
}

export interface SignatureConfig {
  type: ConfigType.signature;
  /** When true, only typed signature is offered (no draw canvas) */
  typedOnly?: boolean;
}

/** Consent acknowledgement: content fields + trailing initials as one visual unit. */
export interface AcknowledgementConfig {
  type: ConfigType.acknowledgement;
  formFields: { [key: string]: FormFieldConfig };
}

export type ConsentFormStatus = 'accepted' | 'rejected';

export enum FormTemplateSchemaType {
  intake = 'intake',
  questionnaire = 'questionnaire',
  referral = 'referral',
  assessment = 'assessment',
  consent = 'consent'
}

export interface FormTemplateSchema {
  name: string;
  id: string;
  type: FormTemplateSchemaType;
  message: string;
  attribution?: string;
  abbreviation: string;
  templateGroups: { [key: string]: FormTemplateGroup };
  supportingFields: {
    [key: string]: FormFieldConfig;
  };
  availableLanguages?: { id: string; label: string }[];
}
